<template>
  <div v-if="completionScore">
    <!-- Overall progress bar -->
    <div class="mb-1">
      <span class="text-sm font-medium" :class="overallTextColor">{{ completionScore.score }}%</span>
    </div>
    <div class="w-full bg-gray-200 rounded-full h-2.5 mb-4">
      <div
        class="h-2.5 rounded-full transition-all duration-500"
        :class="overallBarColor"
        :style="{ width: completionScore.score + '%' }"
      ></div>
    </div>

    <!-- Category accordions -->
    <div v-if="completionScore.categories?.length" class="space-y-1">
      <Disclosure
        v-for="category in completionScore.categories"
        :key="category.key"
        v-slot="{ open }"
        :default-open="shouldBeOpen(category)"
        as="div"
        class="border-b border-gray-100 last:border-b-0"
      >
        <DisclosureButton
          class="flex w-full items-center justify-between py-2 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded"
          :disabled="!hasMissing(category)"
          :class="{ 'cursor-default': !hasMissing(category) }"
        >
          <span class="flex items-center gap-2 text-sm text-gray-700">
            <ChevronRightIcon
              v-if="hasMissing(category)"
              class="h-4 w-4 text-gray-400 transition-transform duration-200"
              :class="{ 'rotate-90': open }"
            />
            <span v-else class="inline-block w-4 h-4"></span>
            {{ categoryLabel(category.key) }}
          </span>
          <span
            class="text-sm font-medium tabular-nums"
            :class="categoryScoreColor(category.score)"
          >{{ category.score }}%</span>
        </DisclosureButton>

        <transition
          enter-active-class="transition duration-100 ease-out"
          enter-from-class="transform -translate-y-1 opacity-0"
          enter-to-class="transform translate-y-0 opacity-100"
          leave-active-class="transition duration-75 ease-in"
          leave-from-class="transform translate-y-0 opacity-100"
          leave-to-class="transform -translate-y-1 opacity-0"
        >
          <DisclosurePanel class="pb-2">
            <ul v-if="hasMissing(category)" class="space-y-1 pl-6">
              <li
                v-for="(item, index) in missingItemsFor(category.key)"
                :key="`${item.key}-${index}`"
              >
                <button
                  type="button"
                  class="flex w-full items-start gap-2 text-left text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded px-2 py-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  @click="onItemClick(item)"
                >
                  <XCircleIcon class="h-4 w-4 text-red-400 flex-shrink-0 mt-0.5" />
                  <span class="flex-1">
                    <span v-if="item.entity?.display_name" class="font-medium text-gray-700">
                      {{ item.entity.display_name }}
                      <span class="text-gray-400">·</span>
                    </span>
                    {{ item.label }}
                  </span>
                  <ChevronRightIcon
                    v-if="item.navigation"
                    class="h-4 w-4 text-gray-300 flex-shrink-0 mt-0.5"
                  />
                </button>
              </li>
            </ul>
          </DisclosurePanel>
        </transition>
      </Disclosure>
    </div>

    <div
      v-if="!hasAnyMissing"
      class="flex items-center gap-2 text-sm text-green-600 mt-3"
    >
      <CheckCircleIcon class="h-5 w-5" />
      <span>{{ $gettext("Dossier complet") }}</span>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { useRouter, useRoute } from "vue-router";
import { Disclosure, DisclosureButton, DisclosurePanel } from "@headlessui/vue";
import {
  XCircleIcon,
  CheckCircleIcon,
  ChevronRightIcon,
} from "@heroicons/vue/24/outline";
import { useGettext } from "vue3-gettext";

const { $gettext } = useGettext();
const router = useRouter();
const route = useRoute();

const props = defineProps({
  completionScore: {
    type: Object,
    default: null,
  },
});

const CATEGORY_LABELS = {
  direct_fields: $gettext("Champs directs"),
  sellers: $gettext("Vendeurs"),
  landlords: $gettext("Bailleurs"),
  properties: $gettext("Propriété(s)"),
  transaction: $gettext("Transaction"),
  lease: $gettext("Bail"),
  documents_and_risk: $gettext("Documents & Risque"),
};

const categoryLabel = (key) => CATEGORY_LABELS[key] || key;

// --- Overall bar colors ---
const overallBarColor = computed(() => {
  const s = props.completionScore?.score ?? 0;
  if (s < 50) return "bg-red-500";
  if (s < 80) return "bg-orange-400";
  return "bg-green-500";
});

const overallTextColor = computed(() => {
  const s = props.completionScore?.score ?? 0;
  if (s < 50) return "text-red-600";
  if (s < 80) return "text-orange-600";
  return "text-green-600";
});

const categoryScoreColor = (score) => {
  if (score < 50) return "text-red-500";
  if (score < 80) return "text-orange-500";
  return "text-green-500";
};

// --- Missing items logic ---
const missingItemsByCategory = computed(() => {
  const map = {};
  (props.completionScore?.missing_items || []).forEach((item) => {
    const key = item.category;
    if (!map[key]) map[key] = [];
    map[key].push(item);
  });
  return map;
});

const missingItemsFor = (key) => missingItemsByCategory.value[key] || [];
const hasMissing = (category) => missingItemsFor(category.key).length > 0;
const shouldBeOpen = (category) => hasMissing(category);

const hasAnyMissing = computed(
  () => (props.completionScore?.missing_items?.length || 0) > 0,
);

// --- Click handler ---
const onItemClick = async (item) => {
  const nav = item.navigation;
  if (!nav) return;

  const sameRoute =
    route.name === nav.route_name &&
    String(route.params?.id) === String(nav.params?.id);

  if (sameRoute) {
    scrollToAnchor(nav.anchor);
  } else {
    await router.push({
      name: nav.route_name,
      params: nav.params,
      query: nav.query || {},
      hash: "#" + nav.anchor,
    });
  }
};

const scrollToAnchor = (anchor) => {
  if (!anchor) return;
  const el = document.getElementById(anchor);
  if (!el) return;
  el.scrollIntoView({ behavior: "smooth", block: "center" });
  // Temporary highlight
  el.classList.add("ring-2", "ring-primary", "ring-offset-2");
  setTimeout(() => {
    el.classList.remove("ring-2", "ring-primary", "ring-offset-2");
  }, 2000);
};
</script>
